import { ref, type Ref } from "vue";

export type FileDropHandlers = {
  onDragEnter: (event: DragEvent) => void;
  onDragOver: (event: DragEvent) => void;
  onDragLeave: (event: DragEvent) => void;
  onDrop: (event: DragEvent) => void;
};

export type UseFileDrop = {
  isDragActive: Ref<boolean>;
  dragHandlers: FileDropHandlers;
};

/**
 * Returns true only for drags that carry actual files (e.g. from the OS file
 * explorer). Internal drags produced by ProseMirror or other Vue components
 * don't include the "Files" data-transfer type, so we ignore them and let the
 * inner handlers deal with them.
 */
const hasFiles = (event: DragEvent): boolean => {
  const types = event.dataTransfer?.types;
  if (!types) return false;
  for (let i = 0; i < types.length; i++) {
    if (types[i] === "Files") return true;
  }
  return false;
};

/**
 * File-drop from the OS into any element. Use the returned handlers in the
 * capture phase so that we intercept drops before descendants (like the
 * ProseMirror editor) can consume them.
 */
export function useFileDrop(
  onFiles: (files: File[]) => void | Promise<void>
): UseFileDrop {
  const isDragActive = ref(false);
  let dragCounter = 0;

  const reset = () => {
    dragCounter = 0;
    isDragActive.value = false;
  };

  const onDragEnter = (event: DragEvent) => {
    if (!hasFiles(event)) return;
    dragCounter++;
    isDragActive.value = true;
  };

  const onDragOver = (event: DragEvent) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
  };

  const onDragLeave = (event: DragEvent) => {
    if (!hasFiles(event)) return;
    dragCounter = Math.max(0, dragCounter - 1);
    if (dragCounter === 0) isDragActive.value = false;
  };

  const onDrop = (event: DragEvent) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    reset();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      void onFiles(Array.from(files));
    }
  };

  return {
    isDragActive,
    dragHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
